import { useEffect, useMemo, useState, type FC } from "react";
import { useNavigate } from "react-router-dom";

import type { MatterDTO } from "../../../../api/types/matter";
import { caseWorkspacePath, matterCaseRef } from "../../../caseIde/routes";
import { useUserProfiles } from "../../../userProfiles/context/UserProfilesContext";
import { useClientProfile } from "../../context/ClientProfileContext";
import type { CasesFilter } from "../../lib/caseStatus";
import { NAV_ITEMS } from "../../navConfig";
import { DEFAULT_CASES_SORT, visibleCases, type CasesSort } from "./casesModel";
import { CasesTable } from "./CasesTable";
import { CasesToolbar } from "./CasesToolbar";

/** The client's cases: a header (sort / status / new case) over the table. */
export const CasesTab: FC = () => {
  const navigate = useNavigate();
  const { clientId, matters, createCase } = useClientProfile();
  const { ensure: ensureProfiles } = useUserProfiles();
  const [filter, setFilter] = useState<CasesFilter>("all");
  const [sort, setSort] = useState<CasesSort>(DEFAULT_CASES_SORT);

  useEffect(() => {
    setFilter("all");
    setSort(DEFAULT_CASES_SORT);
  }, [clientId]);

  const rows = useMemo(
    () => visibleCases(matters.matters, filter, sort),
    [filter, matters.matters, sort]
  );

  useEffect(() => {
    const ids = new Set<string>();
    rows.forEach((m) =>
      (m.assigned_to_user_ids ?? []).forEach((id) => {
        const t = (id ?? "").trim();
        if (t) ids.add(t);
      })
    );
    if (ids.size) ensureProfiles(Array.from(ids));
  }, [ensureProfiles, rows]);

  const openCase = (matter: MatterDTO) => {
    navigate(caseWorkspacePath(matterCaseRef(matter)));
  };

  const openNewCase = () => createCase.open({ returnToOverview: false });

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <CasesToolbar
        title={NAV_ITEMS.cases.label}
        filter={filter}
        onFilterChange={setFilter}
        sort={sort}
        onSortChange={setSort}
        onNewCase={openNewCase}
      />
      <CasesTable
        matters={rows}
        isLoading={matters.isLoading}
        isFiltered={filter !== "all"}
        error={matters.error}
        onRetry={() => matters.refresh()}
        onOpen={openCase}
        onNewCase={openNewCase}
      />
    </div>
  );
};
